import { TensorHandle, SupportedDType } from "./types.js";
import {
  getOrCreatePipeline,
  dispatchCompute,
  calculateWorkgroups,
  syncDevice,
  BufferUsage,
  CONV_SHADER,
  CONV_BACKWARD_SHADER,
  createStorageBuffer,
} from "./utils.js";
import { DeviceManager } from "./device.js";

export class ConvOps {
  constructor(private deviceMgr: DeviceManager) {}

  async conv2d(
    inputId: number,
    weightId: number,
    bias: number[] | null,
    stride: number[],
    padding: number[],
    dilation: number[],
    groups: number,
  ): Promise<TensorHandle> {
    await this.deviceMgr.ensureReady();
    const input = this.deviceMgr.getTensorMeta(inputId);
    const w = this.deviceMgr.getTensorMeta(weightId);
    const [batch, inCh, inH, inW] = input.shape;
    const [outCh, , kernelH, kernelW] = w.shape;
    const padH = padding[0] ?? 0;
    const padW = padding[1] ?? padding[0] ?? 0;
    const strideH = stride[0] ?? 1;
    const strideW = stride[1] ?? stride[0] ?? 1;
    const outH = Math.floor((inH + 2 * padH - kernelH) / strideH + 1);
    const outW = Math.floor((inW + 2 * padW - kernelW) / strideW + 1);
    const total = batch * outCh * outH * outW;

    const out = createStorageBuffer(this.deviceMgr.device!, Math.max(4, total * 4));

    // If bias is null, create a dummy zero bias so the shader can still bind it
    const biasData = bias ?? new Array(outCh).fill(0);
    const biasBuf = createStorageBuffer(this.deviceMgr.device!, Math.max(4, outCh * 4));
    this.deviceMgr.writeBuffer(biasBuf, 0, new Float32Array(biasData));

    const params = new Uint32Array([
      batch, inCh, outCh, inH, inW, outH, outW, kernelH, kernelW,
      strideH, strideW, padH, padW, groups, 0,
    ]);
    const paramBuffer = this.deviceMgr.device!.createBuffer({
      size: params.byteLength,
      usage: BufferUsage.UNIFORM | BufferUsage.COPY_DST,
    });
    this.deviceMgr.writeBuffer(paramBuffer, 0, params);

    const pipeline = await getOrCreatePipeline(CONV_SHADER, "conv2d");
    dispatchCompute(pipeline, [input.buffer, w.buffer, biasBuf, out, paramBuffer], calculateWorkgroups(total));
    await syncDevice();
    paramBuffer.destroy();
    biasBuf.destroy();
    return this.deviceMgr.registerTensorAsHandle(out, [batch, outCh, outH, outW], input.dtype as SupportedDType, total);
  }

  async conv2dInputBackward(
    gradOutputId: number,
    weightId: number,
    inputShape: number[],
    gradOutputShape: number[],
    stride: number[],
    padding: number[],
  ): Promise<TensorHandle> {
    await this.deviceMgr.ensureReady();
    const gradOutput = this.deviceMgr.getTensorMeta(gradOutputId);
    const weight = this.deviceMgr.getTensorMeta(weightId);
    const [batch, inCh, inH, inW] = inputShape;
    const [, outCh, outH, outW] = gradOutputShape;
    const [, , kernelH, kernelW] = weight.shape;
    const strideH = stride[0] ?? 1;
    const strideW = stride[1] ?? stride[0] ?? 1;
    const padH = padding[0] ?? 0;
    const padW = padding[1] ?? padding[0] ?? 0;
    const groups = 1;
    const total = batch * inCh * inH * inW;

    const gradInput = createStorageBuffer(this.deviceMgr.device!, Math.max(4, total * 4));

    const params = new Uint32Array([
      batch, inCh, outCh, inH, inW, outH, outW, kernelH, kernelW,
      strideH, strideW, padH, padW, groups, 0,
    ]);
    const paramBuffer = this.deviceMgr.device!.createBuffer({
      size: params.byteLength,
      usage: BufferUsage.UNIFORM | BufferUsage.COPY_DST,
    });
    this.deviceMgr.writeBuffer(paramBuffer, 0, params);

    // conv2d_input_backward uses bindings: 0=grad_output, 1=weight, 2=grad_input, 3=params
    const pipeline = await getOrCreatePipeline(CONV_BACKWARD_SHADER, "conv2d_input_backward");
    dispatchCompute(pipeline, [
      { binding: 0, resource: { buffer: gradOutput.buffer, offset: 0, size: gradOutput.buffer.size } },
      { binding: 1, resource: { buffer: weight.buffer, offset: 0, size: weight.buffer.size } },
      { binding: 2, resource: { buffer: gradInput, offset: 0, size: gradInput.size } },
      { binding: 3, resource: { buffer: paramBuffer, offset: 0, size: paramBuffer.size } },
    ], calculateWorkgroups(total));
    await syncDevice();
    paramBuffer.destroy();
    return this.deviceMgr.registerTensorAsHandle(gradInput, [batch, inCh, inH, inW], gradOutput.dtype as SupportedDType, total);
  }

  async conv2dWeightBackward(
    gradOutputId: number,
    inputId: number,
    weightShape: number[],
    gradOutputShape: number[],
    inputShape: number[],
    stride: number[],
    padding: number[],
  ): Promise<TensorHandle> {
    await this.deviceMgr.ensureReady();
    const gradOutput = this.deviceMgr.getTensorMeta(gradOutputId);
    const input = this.deviceMgr.getTensorMeta(inputId);
    const [outCh, inCh, kernelH, kernelW] = weightShape;
    const [batch] = inputShape;
    const [, , outH, outW] = gradOutputShape;
    const strideH = stride[0] ?? 1;
    const strideW = stride[1] ?? stride[0] ?? 1;
    const padH = padding[0] ?? 0;
    const padW = padding[1] ?? padding[0] ?? 0;
    const groups = 1;
    const total = outCh * inCh * kernelH * kernelW;

    const gradWeight = createStorageBuffer(this.deviceMgr.device!, Math.max(4, total * 4));

    const params = new Uint32Array([
      inputShape[0], inputShape[1], outCh, inputShape[2], inputShape[3], outH, outW, kernelH, kernelW,
      strideH, strideW, padH, padW, groups, 0,
    ]);
    const paramBuffer = this.deviceMgr.device!.createBuffer({
      size: params.byteLength,
      usage: BufferUsage.UNIFORM | BufferUsage.COPY_DST,
    });
    this.deviceMgr.writeBuffer(paramBuffer, 0, params);

    // conv2d_weight_backward uses bindings: 0=grad_output, 3=params, 4=grad_weight, 5=input
    const pipeline = await getOrCreatePipeline(CONV_BACKWARD_SHADER, "conv2d_weight_backward");
    dispatchCompute(pipeline, [
      { binding: 0, resource: { buffer: gradOutput.buffer, offset: 0, size: gradOutput.buffer.size } },
      { binding: 3, resource: { buffer: paramBuffer, offset: 0, size: paramBuffer.size } },
      { binding: 4, resource: { buffer: gradWeight, offset: 0, size: gradWeight.size } },
      { binding: 5, resource: { buffer: input.buffer, offset: 0, size: input.buffer.size } },
    ], calculateWorkgroups(total));
    await syncDevice();
    paramBuffer.destroy();
    return this.deviceMgr.registerTensorAsHandle(gradWeight, [outCh, inCh, kernelH, kernelW], gradOutput.dtype as SupportedDType, total);
  }

  async conv2dBiasBackward(
    gradOutputId: number,
    outCh: number,
    gradOutputShape: number[],
  ): Promise<TensorHandle> {
    await this.deviceMgr.ensureReady();
    const gradOutput = this.deviceMgr.getTensorMeta(gradOutputId);
    const [batch, , outH, outW] = gradOutputShape;
    const strideH = 1;
    const strideW = 1;
    const padH = 0;
    const padW = 0;
    const groups = 1;
    // Reconstruct inCh/inCh from gradOutput shape (not really used for bias backward, but shader expects them)
    const inCh = 1; // placeholder
    const inH = outH; // placeholder
    const inW = outW; // placeholder
    const total = outCh;

    const gradBias = createStorageBuffer(this.deviceMgr.device!, Math.max(4, total * 4));

    const params = new Uint32Array([
      batch, inCh, outCh, inH, inW, outH, outW, 1, 1,
      strideH, strideW, padH, padW, groups, 0,
    ]);
    const paramBuffer = this.deviceMgr.device!.createBuffer({
      size: params.byteLength,
      usage: BufferUsage.UNIFORM | BufferUsage.COPY_DST,
    });
    this.deviceMgr.writeBuffer(paramBuffer, 0, params);

    // conv2d_bias_backward uses bindings: 0=grad_output, 3=params, 4=grad_bias
    const pipeline = await getOrCreatePipeline(CONV_BACKWARD_SHADER, "conv2d_bias_backward");
    dispatchCompute(pipeline, [
      { binding: 0, resource: { buffer: gradOutput.buffer, offset: 0, size: gradOutput.buffer.size } },
      { binding: 3, resource: { buffer: paramBuffer, offset: 0, size: paramBuffer.size } },
      { binding: 4, resource: { buffer: gradBias, offset: 0, size: gradBias.size } },
    ], calculateWorkgroups(total));
    await syncDevice();
    paramBuffer.destroy();
    return this.deviceMgr.registerTensorAsHandle(gradBias, [outCh], gradOutput.dtype as SupportedDType, total);
  }
}
